import React from 'react'
import LoadingSpinner from '../LoadingSpinner'

interface OptionGroup {
  option_group: {
    id: number
    name: string
    min_select: number
    max_select: number
    sort_order: number
    option_items?: {
      id: number
      label: string
      price_delta_cents: number
      default_selected: boolean
      sort_order: number
      /** The linked product's live sold-out mark: shown disabled, never selectable (the API refuses it). */
      sold_out?: boolean
    }[]
  }
}

interface CustomizeStepProps {
  optionGroups: OptionGroup[]
  selectedOptions: Record<number, number[]>
  onOptionsChange: (optionGroupId: number, selectedItemIds: number[]) => void
  loading: boolean
}

const CustomizeStep: React.FC<CustomizeStepProps> = ({
  optionGroups,
  selectedOptions,
  onOptionsChange,
  loading
}) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (optionGroups.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="text-6xl mb-4">✅</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No customization needed</h3>
        <p className="text-gray-500">This variant doesn't require any options.</p>
      </div>
    )
  }

  const formatPrice = (priceCents: number) => {
    if (priceCents === 0) return ''
    const sign = priceCents > 0 ? '+' : ''
    return `${sign}$${(priceCents / 100).toFixed(2)}`
  }

  const handleOptionToggle = (optionGroupId: number, optionItemId: number) => {
    const currentSelection = selectedOptions[optionGroupId] || []
    const optionGroup = optionGroups.find(og => og.option_group.id === optionGroupId)?.option_group
    
    if (!optionGroup) return
    // A sold-out option cannot be chosen (its button is disabled; the API would refuse it anyway).
    if (!currentSelection.includes(optionItemId) && (optionGroup.option_items || []).some(i => i.id === optionItemId && i.sold_out)) return

    let newSelection: number[]
    
    if (currentSelection.includes(optionItemId)) {
      // Remove the option
      newSelection = currentSelection.filter(id => id !== optionItemId)
    } else {
      // Add the option (check max_select constraint)
      if (optionGroup.max_select === 1) {
        // Single selection - replace current selection
        newSelection = [optionItemId]
      } else {
        // Multiple selection - add to current selection
        if (currentSelection.length < optionGroup.max_select) {
          newSelection = [...currentSelection, optionItemId]
        } else {
          // Already at max, don't add
          return
        }
      }
    }
    
    onOptionsChange(optionGroupId, newSelection)
  }

  // Sort option groups by sort_order, then by required status
  const sortedOptionGroups = [...optionGroups].sort((a, b) => {
    // Required groups first (min_select >= 1)
    const aRequired = a.option_group.min_select >= 1
    const bRequired = b.option_group.min_select >= 1
    
    if (aRequired && !bRequired) return -1
    if (!aRequired && bRequired) return 1
    
    // Then by sort_order
    return (a.option_group.sort_order || 0) - (b.option_group.sort_order || 0)
  })


  // Calculate total price delta from selected options
  const totalPriceDelta = Object.entries(selectedOptions).reduce((total, [groupId, itemIds]) => {
    const group = optionGroups.find(og => og.option_group.id === Number(groupId))?.option_group
    if (!group) return total
    
    const groupTotal = itemIds.reduce((groupTotal, itemId) => {
      const item = group.option_items?.find(item => item.id === itemId)
      return groupTotal + (item?.price_delta_cents || 0)
    }, 0)
    
    return total + groupTotal
  }, 0)

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Customize Your Order</h3>
        <p className="text-sm text-gray-500 mb-6">
          Choose your preferences and add-ons
        </p>
        
        {/* Show current selections summary */}
        {Object.keys(selectedOptions).length > 0 && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="text-sm font-medium text-blue-900 mb-2">Current Selections:</h4>
            <div className="space-y-1">
              {Object.entries(selectedOptions).map(([groupId, itemIds]) => {
                const group = optionGroups.find(og => og.option_group.id === Number(groupId))?.option_group
                if (!group || itemIds.length === 0) return null
                
                return (
                  <div key={groupId} className="text-sm text-blue-800">
                    <span className="font-medium">{group.name}:</span>{' '}
                    {itemIds.map(itemId => {
                      const item = group.option_items?.find(item => item.id === itemId)
                      return item?.label
                    }).filter(Boolean).join(', ')}
                  </div>
                )
              })}
            </div>
            {totalPriceDelta !== 0 && (
              <div className="mt-2 text-sm font-medium text-blue-900">
                Total: {formatPrice(totalPriceDelta)}
              </div>
            )}
          </div>
        )}
      </div>

      {sortedOptionGroups.map((optionGroupWrapper) => {
        const group = optionGroupWrapper.option_group
        const currentSelection = selectedOptions[group.id] || []
        const isRequired = group.min_select >= 1
        const isComplete = currentSelection.length >= group.min_select
        
        
        return (
          <div key={group.id} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-gray-900">
                {group.name}
                {isRequired && (
                  <span className="text-red-500 ml-1">*</span>
                )}
              </h4>
              <div className="text-sm text-gray-500">
                {group.min_select === group.max_select 
                  ? `${group.min_select} required`
                  : `${group.min_select}-${group.max_select} selections`
                }
              </div>
            </div>


            {!isComplete && isRequired && (
              <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                Please select at least {group.min_select} option{group.min_select > 1 ? 's' : ''}
              </div>
            )}

            {(group.option_items || []).length > 0 && (group.option_items || []).every(i => i.sold_out) && (
              <div className="mb-3 text-sm text-gray-500">
                Everything in this group is sold out{isRequired ? ' — this item cannot be ordered right now.' : '.'}
              </div>
            )}

            <div className="space-y-2">
              {(!group.option_items || group.option_items.length === 0) ? (
                <div className="p-3 text-center text-gray-500 bg-gray-50 border border-gray-200 rounded">
                  No options available for this group
                </div>
              ) : (
                (group.option_items || [])
                  .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                  .map((item) => {
                  const soldOut = !!item.sold_out
                  const isSelected = currentSelection.includes(item.id)
                  const isDisabled = !isSelected && 
                    currentSelection.length >= group.max_select && 
                    group.max_select > 1
                  
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleOptionToggle(group.id, item.id)}
                      disabled={isDisabled || soldOut}
                      className={`w-full p-3 border rounded-lg text-left transition-all duration-200 cursor-pointer ${
                        soldOut
                          ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                          : isSelected
                          ? 'border-primary-500 bg-primary-50'
                          : isDisabled
                          ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 hover:shadow-sm'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          {group.max_select === 1 ? (
                            <div className={`w-4 h-4 rounded-full border-2 mr-3 ${
                              isSelected 
                                ? 'border-primary-500 bg-primary-500' 
                                : 'border-gray-300'
                            }`}>
                              {isSelected && (
                                <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5" />
                              )}
                            </div>
                          ) : (
                            <div className={`w-4 h-4 border-2 rounded mr-3 ${
                              isSelected 
                                ? 'border-primary-500 bg-primary-500' 
                                : 'border-gray-300'
                            }`}>
                              {isSelected && (
                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>
                          )}
                          <div>
                            <span className="font-medium text-gray-900">{item.label}</span>
                            {soldOut && <div className="text-xs font-semibold uppercase text-gray-500">Sold out</div>}
                          </div>
                        </div>
                        {item.price_delta_cents !== 0 && (
                          <span className={`text-sm font-medium ${
                            item.price_delta_cents > 0 ? 'text-red-600' : 'text-green-600'
                          }`}>
                            {formatPrice(item.price_delta_cents)}
                          </span>
                        )}
                      </div>
                    </button>
                  )
                })
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default CustomizeStep
